import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/supabaseService';
import { storageService } from '../../services/storageService';
import { Upload, X, Image as ImageIcon, Shield, Mail, User, CreditCard, ChevronRight } from 'lucide-react';

const DashboardAccountPage = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const avatarInputRef = useRef(null);

  const avatarUrl = userProfile?.avatarUrl ?? userProfile?.avatar_url;

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setAvatarError('');
    setAvatarLoading(true);
    try {
      const url = await storageService.uploadAvatar(user.id, file);
      await profileService.updateProfileMedia(user.id, { avatar_url: url });
      await refreshProfile();
    } catch (err) {
      setAvatarError(err.message || 'Upload failed');
    } finally {
      setAvatarLoading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-bold text-3xl text-white">My Account</h1>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-white/5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          ID: {user?.id?.slice(0, 8)}...
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="p-6 rounded-3xl bg-slate-800/50 border border-white/10 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-pink"></div>
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 rounded-2xl bg-slate-700 overflow-hidden flex items-center justify-center border-4 border-slate-900 shadow-xl">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-slate-500" />
                )}
              </div>
              <button 
                onClick={() => avatarInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-lg bg-brand-pink text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              >
                <Upload className="h-4 w-4" />
              </button>
              <input ref={avatarInputRef} type="file" className="hidden" onChange={handleAvatarChange} />
            </div>
            <h2 className="font-bold text-xl text-white mb-1">
              {userProfile?.displayName ?? userProfile?.display_name ?? 'Stranger'}
            </h2>
            <p className="text-sm text-slate-400 mb-4">{user?.email}</p>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
              userProfile?.subscriptionStatus === 'premium' ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-500'
            }`}>
              {userProfile?.subscriptionStatus === 'premium' ? 'Premium Member' : 'Free Account'}
            </div>
          </div>

          <div className="p-6 rounded-3xl bg-slate-800/50 border border-white/10 space-y-4">
            <h3 className="font-bold text-white text-sm flex items-center gap-2">
              <Shield className="h-4 w-4 text-brand-pink" />
              Account Security
            </h3>
            <button className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors text-sm text-slate-300">
              Change Password
              <ChevronRight className="h-4 w-4 text-slate-600" />
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors text-sm text-slate-300">
              Two-Factor Auth
              <span className="text-[10px] bg-slate-700 px-2 py-0.5 rounded text-slate-400">Off</span>
            </button>
          </div>
        </div>

        {/* Right Column: Details & Subscription */}
        <div className="lg:col-span-2 space-y-8">
          {/* Subscription Section */}
          <section className="p-8 rounded-3xl bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-white/10 shadow-2xl relative overflow-hidden">
             {userProfile?.subscriptionStatus === 'premium' && (
               <div className="absolute top-4 right-4 animate-pulse">
                 <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_#22c55e]"></div>
               </div>
             )}
            
            <div className="flex items-start justify-between mb-8">
              <div>
                <h3 className="text-2xl font-black text-white mb-1 flex items-center gap-2">
                  <CreditCard className="h-6 w-6 text-brand-pink" />
                  My Subscription
                </h3>
                <p className="text-slate-400 text-sm">Manage your membership and billing</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black mb-1">Current Plan</p>
                <p className="text-lg font-bold text-white">
                  {userProfile?.subscriptionStatus === 'premium' ? 'Premium Full Access' : 'Free Standard'}
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black mb-1">
                  {userProfile?.subscriptionStatus === 'premium' ? 'Renews On' : 'Status'}
                </p>
                <p className="text-lg font-bold text-white">
                  {userProfile?.subscriptionStatus === 'premium' ? (
                    new Date(userProfile.subscriptionEndAt).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'long', year: 'numeric'
                    })
                  ) : 'Not Subscribed'}
                </p>
              </div>
            </div>

            {userProfile?.subscriptionStatus !== 'premium' ? (
              <button 
                onClick={() => window.location.href = '/payment'}
                className="w-full py-4 rounded-2xl bg-brand-pink text-white font-bold text-lg hover:bg-brand-pink-dark transition-all shadow-xl shadow-brand-pink/30 flex items-center justify-center gap-2"
              >
                Upgrade to Premium
                <ChevronRight className="h-5 w-5" />
              </button>
            ) : (
              <div className="p-4 rounded-2xl bg-green-500/5 border border-green-500/10 text-green-400 text-sm text-center">
                Your premium benefits are active. Thank you for being a member!
              </div>
            )}
          </section>

          {/* Personal Information */}
          <section className="p-8 rounded-3xl bg-slate-800/50 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Mail className="h-5 w-5 text-brand-pink" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Email Address</label>
                <p className="text-white font-medium">{user?.email}</p>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Display Name</label>
                <p className="text-white font-medium">{userProfile?.displayName ?? userProfile?.display_name ?? '—'}</p>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Member Since</label>
                <p className="text-white font-medium">
                  {new Date(user?.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'long', year: 'numeric'
                  })}
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Account Role</label>
                <p className="text-white font-medium capitalize">{userProfile?.role || 'User'}</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DashboardAccountPage;
